import Service, { service } from '@ember/service'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'

/**
 * This service is the canonical reference for driving test grades being displayed
 * to teachers. Allows translation and interpretation of driving test grades.
 * Note that a Driving Test Grade is not the same thing as a Grade or a grade position.
 * Teachers don't care that the driving test grade is "Early", "1" and "2" written on
 * the interactive. What they want to know is what these correspond to in the school's
 * grade sets (ie Kindergarten, grade 1, grade 2, etc).
 *
 * NOTE: This service is only valid for usage when dealing with MATHS driving tests. Reading driving tests do not have
 * "grades", some data from the backend represents this as drivingTestGrade = null.
 */

export default class MathsDrivingTestsService extends Service {
  @service gradeSets!: GradeSetsService

  /**
   * gradeSetPosition is position within grade-set - 1, 2, 3
   * drivingTestGrade is the index of the grade in the driving test - 0, 1, 2
   * Which corresponds to 0 as "Early", 1 as "Level 1" and 2 as "Level 2".
   * name is the Grade Name of the gradeSetPosition
   * fullName is the full translated grade name of the gradeSetPosition (displayed to users)
   */
  get drivingTestData() {
    return [
      {
        gradeSetPosition: 1,
        drivingTestGrade: 0,
        name: this.gradeNameForPosition(1),
        fullName: this.fullGradeNameForPosition(1),
      },
      {
        gradeSetPosition: 2,
        drivingTestGrade: 1,
        name: this.gradeNameForPosition(2),
        fullName: this.fullGradeNameForPosition(2),
      },
      {
        gradeSetPosition: 3,
        drivingTestGrade: 2,
        name: this.gradeNameForPosition(3),
        fullName: this.fullGradeNameForPosition(3),
      },
    ]
  }

  gradeNameForPosition(position: number) {
    return this.gradeSets.nameFromGradeSetByPosition(position)
  }

  fullGradeNameForPosition(position: number) {
    return this.gradeSets.fullNameFromGradeSetByPosition(position)
  }

  findByDrivingTestGrade(drivingTestGrade: number) {
    return this.drivingTestData.find((dtGradeItem) => dtGradeItem.drivingTestGrade === drivingTestGrade)
  }
}

declare module '@ember/service' {
  interface Registry {
    'maths-driving-tests': MathsDrivingTestsService
  }
}
